import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { localizeDict } from "@/lib/localText";
import { subscribeNativePrompts } from "@/lib/nativePrompts";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Cookie } from "lucide-react";
import { phoneNav } from "@/lib/platform";
import { nativeConsentFlow } from "@/lib/welcomeConsent";

const STORAGE_KEY = "yalola.cookie_consent";
const CAT_KEY = "yalola.cookie_categories";
export const OPEN_COOKIE_PREFS_EVENT = "yalola:open-cookie-prefs";

const L10N = {
  fr: {
    title: "Respect de ta vie privée",
    text: "Yalola utilise des cookies essentiels pour te connecter et des cookies analytiques (optionnels) pour améliorer l'expérience. Consulte notre",
    privacy: "Politique de confidentialité",
    acceptAll: "Tout accepter", reject: "Refuser", customize: "Personnaliser", save: "Enregistrer mes choix",
    essential: "Essentiels", essentialDesc: "Nécessaires au fonctionnement (connexion, sécurité). Toujours actifs.",
    analytics: "Analytiques", analyticsDesc: "Nous aident à comprendre l'usage pour améliorer Yalola.",
    always: "Toujours actif",
  },
  en: {
    title: "Respecting your privacy",
    text: "Yalola uses essential cookies to keep you signed in and optional analytics cookies to improve the experience. See our",
    privacy: "Privacy Policy",
    acceptAll: "Accept all", reject: "Decline", customize: "Customize", save: "Save my choices",
    essential: "Essential", essentialDesc: "Required to run the app (login, security). Always on.",
    analytics: "Analytics", analyticsDesc: "Help us understand usage to improve Yalola.",
    always: "Always on",
  },
  es: {
    title: "Respetamos tu privacidad",
    text: "Yalola usa cookies esenciales para mantener tu sesión y cookies analíticas opcionales para mejorar la experiencia. Consulta nuestra",
    privacy: "Política de privacidad",
    acceptAll: "Aceptar todo", reject: "Rechazar", customize: "Personalizar", save: "Guardar mis opciones",
    essential: "Esenciales", essentialDesc: "Necesarias para funcionar (sesión, seguridad). Siempre activas.",
    analytics: "Analíticas", analyticsDesc: "Nos ayudan a entender el uso para mejorar Yalola.",
    always: "Siempre activo",
  },
  pt: {
    title: "Respeitamos a tua privacidade",
    text: "A Yalola usa cookies essenciais para manter a tua sessão e cookies analíticos opcionais para melhorar a experiência. Consulta a nossa",
    privacy: "Política de privacidade",
    acceptAll: "Aceitar tudo", reject: "Recusar", customize: "Personalizar", save: "Guardar as minhas opções",
    essential: "Essenciais", essentialDesc: "Necessários ao funcionamento (sessão, segurança). Sempre ativos.",
    analytics: "Analíticos", analyticsDesc: "Ajudam-nos a perceber o uso para melhorar a Yalola.",
    always: "Sempre ativo",
  },
};

export const CookieBanner = () => {
  const { lang, user, config } = useApp();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  // Native app: hold the banner while a system prompt (ATT / consent / push) is on screen.
  const [promptsBusy, setPromptsBusy] = useState(false);
  useEffect(() => subscribeNativePrompts(setPromptsBusy), []);
  // App native : pas de cookies (la pub est régie par le consentement UMP/AdMob séparé) → jamais de bandeau.
  const nativeFlow = nativeConsentFlow();

  useEffect(() => {
    if (nativeFlow) return;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const cats = JSON.parse(localStorage.getItem(CAT_KEY) || "{}");
      if (typeof cats.analytics === "boolean") setAnalytics(cats.analytics);
      // Only surface the consent banner once the user is signed in AND on the
      // feed — never on the landing page, so it can't block the PWA install popup.
      if (!stored && user && location.pathname === "/") setVisible(true);
      else if (stored) setVisible(false);
    } catch { /* noop */ }
  }, [user, location.pathname, nativeFlow]);

  useEffect(() => {
    const open = () => { setCustomizing(true); setVisible(true); };
    window.addEventListener(OPEN_COOKIE_PREFS_EVENT, open);
    return () => window.removeEventListener(OPEN_COOKIE_PREFS_EVENT, open);
  }, []);

  const persist = (value, categories) => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
      localStorage.setItem(CAT_KEY, JSON.stringify(categories));
    } catch { /* noop */ }
    if (user) { api.post("/users/consent", { type: "cookies", value, categories }).catch(() => {}); }
    setVisible(false);
    setCustomizing(false);
  };

  const acceptAll = () => persist("accepted", { essential: true, analytics: true });
  const rejectAll = () => persist("declined", { essential: true, analytics: false });
  const saveChoices = () => persist("custom", { essential: true, analytics });

  if (!visible || promptsBusy || nativeFlow) return null;
  const tr = localizeDict(L10N, lang, config?.translations, "cookies");

  return (
    <div className={`fixed inset-x-0 z-[60] p-3 sm:p-4 flex justify-center pointer-events-none bottom-[calc(3.5rem+var(--sa-bottom,0px))] ${phoneNav() ? "" : "md:bottom-0 md:pb-[calc(1rem+var(--sa-bottom,0px))]"}`} data-testid="cookie-banner">
      <div
        className="pointer-events-auto w-full max-w-3xl rounded-2xl border shadow-xl p-4 sm:p-5"
        style={{ background: "rgba(255,255,255,0.94)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)", borderColor: "var(--yalola-border)" }}
      >
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="h-10 w-10 rounded-full grid place-items-center shrink-0" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}>
            <Cookie size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm mb-0.5" style={{ color: "var(--yalola-text)" }}>{tr.title}</div>
            <p className="text-sm leading-relaxed" style={{ color: "var(--yalola-text-muted)" }}>
              {tr.text}{" "}
              <Link to="/legal/privacy" className="font-semibold underline" style={{ color: "var(--yalola-primary)" }} data-testid="cookie-privacy-link">{tr.privacy}</Link>.
            </p>

            {customizing && (
              <div className="mt-3 space-y-2" data-testid="cookie-categories">
                <div className="flex items-center gap-3 rounded-xl border p-3" style={{ borderColor: "var(--yalola-border)" }}>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{tr.essential}</div>
                    <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{tr.essentialDesc}</div>
                  </div>
                  <span className="text-xs font-medium" style={{ color: "var(--yalola-primary)" }}>{tr.always}</span>
                </div>
                <div className="flex items-center gap-3 rounded-xl border p-3" style={{ borderColor: "var(--yalola-border)" }}>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{tr.analytics}</div>
                    <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{tr.analyticsDesc}</div>
                  </div>
                  <Switch checked={analytics} onCheckedChange={setAnalytics} data-testid="cookie-cat-analytics" />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 justify-end mt-3">
          {!customizing && (
            <Button variant="ghost" className="rounded-full" onClick={() => setCustomizing(true)} data-testid="cookie-customize">{tr.customize}</Button>
          )}
          <Button variant="outline" className="rounded-full" onClick={rejectAll} data-testid="cookie-decline">{tr.reject}</Button>
          {customizing ? (
            <Button className="pill-primary rounded-full" onClick={saveChoices} data-testid="cookie-save">{tr.save}</Button>
          ) : (
            <Button className="pill-primary rounded-full" onClick={acceptAll} data-testid="cookie-accept">{tr.acceptAll}</Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
